import type { History } from 'history'
import type { ReactElement } from 'react'

import { FlowPage } from '@monthio/design-system'
import { LoadingScreen } from '@monthio/react-components'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useBudgetCreatorModule } from './useBudgetCreatorModule'

export interface BudgetCreatorProps {
	history: History
}

enum BudgetCreatorInternalState {
	IDLE = 'idle',
	REDIRECTING_BACK_TO_CLIENT_WEBSITE = 'redirectingBackToClientWebsite',
}

enum BudgetCreationStatusEnum {
	IDLE = 'idle',
	CREATING = 'creating',
	CREATE_SUCCESS = 'create_success',
	CREATE_FAILURE = 'create_failure',
}

export const BudgetCreator = (): ReactElement => {
	const { t } = useTranslation()
	const { finish, config } = useBudgetCreatorModule()

	const [budgetOutputStatus, setBudgetOutputStatus] =
		useState<BudgetCreationStatusEnum>(BudgetCreationStatusEnum.IDLE)

	const [budgetCreatorState] = useState<BudgetCreatorInternalState>(
		BudgetCreatorInternalState.IDLE,
	)

	const createBudgetOutput = useCallback(async () => {
		try {
			setBudgetOutputStatus(BudgetCreationStatusEnum.CREATING)
			await config.finishSession()
			setBudgetOutputStatus(BudgetCreationStatusEnum.CREATE_SUCCESS)
		} catch (error) {
			setBudgetOutputStatus(BudgetCreationStatusEnum.CREATE_FAILURE)
		}
	}, [])

	useEffect(() => {
		if (budgetOutputStatus === BudgetCreationStatusEnum.IDLE) {
			createBudgetOutput()
		}
	}, [budgetOutputStatus, createBudgetOutput])

	useEffect(() => {
		if (budgetOutputStatus === BudgetCreationStatusEnum.CREATE_SUCCESS) {
			finish()
		}
	}, [budgetOutputStatus, finish])

	if (budgetOutputStatus === BudgetCreationStatusEnum.CREATING) {
		return (
			<FlowPage>
				<FlowPage.Content $isCentered>
					<LoadingScreen />
				</FlowPage.Content>
			</FlowPage>
		)
	}

	if (budgetOutputStatus === BudgetCreationStatusEnum.CREATE_FAILURE) {
		return (
			<FlowPage>
				<FlowPage.Content $isCentered>
					<FlowPage.Header title={t('budgetCreator-failure-title')} />
				</FlowPage.Content>
			</FlowPage>
		)
	}

	if (
		budgetCreatorState ===
		BudgetCreatorInternalState.REDIRECTING_BACK_TO_CLIENT_WEBSITE
	) {
		return (
			<FlowPage>
				<FlowPage.Content $isCentered>
					<LoadingScreen title={t('budgetCreator-redirecting-text')} />
				</FlowPage.Content>
			</FlowPage>
		)
	}

	return (
		<FlowPage>
			<FlowPage.Content $isCentered>
				<LoadingScreen />
			</FlowPage.Content>
		</FlowPage>
	)
}
